import { Controller } from "@hotwired/stimulus";

// Scroll-driven parallax for the starfield of the Space Dragon header (docs/architecture/space-dragon.md). Each layer
// carries its rate in `data-rate`: the farther the stars, the smaller the rate, so they trail the page. Without script,
// and under reduced motion, the layers stay where CSS draws them.
const MAX_SCROLL = 110;

export default class extends Controller<HTMLElement> {
	static targets = ["layer"];
	declare readonly layerTargets: HTMLElement[];

	#frame = 0;
	#observer?: IntersectionObserver;
	#update = () => {
		this.#frame = 0;
		const scrolled = Math.min(window.scrollY, MAX_SCROLL);
		for (const layer of this.layerTargets) {
			layer.style.transform = `translate3d(0, ${(scrolled * Number(layer.dataset.rate)).toFixed(1)}px, 0)`;
		}
	};
	#onScroll = () => {
		if (!this.#frame) this.#frame = requestAnimationFrame(this.#update);
	};

	connect() {
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
		this.#observer = new IntersectionObserver(([entry]) => {
			if (entry.isIntersecting) {
				window.addEventListener("scroll", this.#onScroll, { passive: true });
				this.#update();
			} else {
				window.removeEventListener("scroll", this.#onScroll);
			}
		});
		this.#observer.observe(this.element);
	}

	disconnect() {
		this.#observer?.disconnect();
		window.removeEventListener("scroll", this.#onScroll);
		cancelAnimationFrame(this.#frame);
		this.#frame = 0;
	}
}
